import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Play, Pause, Square, Target, Zap, History, BarChart3, 
  Plus, Trash2, RefreshCw, Copy, ExternalLink, Bot, 
  CheckCircle2, XCircle, Clock, AlertCircle, Terminal,
  Power, Settings, Activity, Database, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sidebar Navigation
const Sidebar = () => {
  const location = useLocation();
  
  const navItems = [
    { path: "/", icon: BarChart3, label: "Dashboard" },
    { path: "/control", icon: Power, label: "Bot Control" },
    { path: "/targets", icon: Target, label: "Targets" },
    { path: "/generator", icon: Zap, label: "Generator" },
    { path: "/tasks", icon: Activity, label: "Tasks" },
    { path: "/history", icon: History, label: "History" },
  ];

  return (
    <aside className="w-64 bg-zinc-950 border-r border-zinc-800 min-h-screen p-4 fixed left-0 top-0" data-testid="sidebar">
      <div className="flex items-center gap-3 mb-8 px-2">
        <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center">
          <Bot className="h-6 w-6 text-green-500" />
        </div>
        <div>
          <h1 className="font-mono font-bold text-lg tracking-tight text-white">RegBot</h1>
          <span className="text-xs text-zinc-500 font-mono">v1.0.0</span>
        </div>
      </div>
      
      <nav className="space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 group ${
                isActive 
                  ? "bg-green-500/10 text-green-500 border-l-2 border-green-500" 
                  : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
              }`}
            >
              <item.icon className={`h-5 w-5 ${isActive ? "text-green-500" : "text-zinc-500 group-hover:text-white"}`} />
              <span className="font-mono text-sm tracking-wide">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      
      <div className="absolute bottom-4 left-4 right-4">
        <div className="p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2 mb-2">
            <Terminal className="h-4 w-4 text-zinc-500" />
            <span className="text-xs text-zinc-500 font-mono">SYSTEM</span>
          </div>
          <p className="text-xs text-zinc-600 font-mono">Bot ID: REG-001</p>
        </div>
      </div>
    </aside>
  );
};

// Dashboard Page
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/logs?limit=20`)
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 text-green-500 animate-spin" />
      </div>
    );
  }

  const getLogIcon = (level) => {
    switch (level) {
      case "SUCCESS": return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "ERROR": return <XCircle className="h-4 w-4 text-red-500" />;
      case "WARNING": return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default: return <Activity className="h-4 w-4 text-blue-500" />;
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-2xl font-bold tracking-tight text-white">
          {"> "}DASHBOARD_OVERVIEW
        </h2>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={fetchData}
          className="text-zinc-400 hover:text-white"
          data-testid="refresh-dashboard"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-dark" data-testid="stat-success-rate">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-mono tracking-wider mb-1">SUCCESS_RATE</p>
                <p className="text-3xl font-mono font-bold text-green-500">
                  {stats?.success_rate || 0}%
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              </div>
            </div>
            <Progress value={stats?.success_rate || 0} className="h-1 mt-4 bg-zinc-800" />
          </CardContent>
        </Card>

        <Card className="card-dark" data-testid="stat-total-registered">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-mono tracking-wider mb-1">TOTAL_REGISTERED</p>
                <p className="text-3xl font-mono font-bold text-blue-500">
                  {stats?.total_registrations || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Database className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark" data-testid="stat-pending-tasks">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-mono tracking-wider mb-1">PENDING_TASKS</p>
                <p className="text-3xl font-mono font-bold text-yellow-500">
                  {stats?.pending_tasks || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark" data-testid="stat-bot-status">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-mono tracking-wider mb-1">BOT_STATUS</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`h-3 w-3 rounded-full ${
                    stats?.bot_status?.is_running 
                      ? stats?.bot_status?.is_paused 
                        ? "bg-yellow-500" 
                        : "bg-green-500 pulse-glow"
                      : "bg-zinc-600"
                  }`} />
                  <span className="font-mono text-lg font-bold text-white">
                    {stats?.bot_status?.is_running 
                      ? stats?.bot_status?.is_paused 
                        ? "PAUSED" 
                        : "RUNNING"
                      : "STOPPED"}
                  </span>
                </div>
              </div>
              <div className="h-12 w-12 rounded-lg bg-zinc-800 flex items-center justify-center">
                <Bot className="h-6 w-6 text-zinc-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quick Stats */}
        <Card className="card-dark" data-testid="quick-stats">
          <CardHeader className="pb-3">
            <CardTitle className="font-mono text-sm text-zinc-400 tracking-wider">
              {"// "}QUICK_STATS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 font-mono mb-1">Active Targets</p>
                <p className="text-2xl font-mono font-bold text-white">{stats?.active_targets || 0}</p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 font-mono mb-1">Total Targets</p>
                <p className="text-2xl font-mono font-bold text-white">{stats?.total_targets || 0}</p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 font-mono mb-1">Successful</p>
                <p className="text-2xl font-mono font-bold text-green-500">{stats?.success_tasks || 0}</p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 font-mono mb-1">Failed</p>
                <p className="text-2xl font-mono font-bold text-red-500">{stats?.failed_tasks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live Logs */}
        <Card className="card-dark" data-testid="live-logs">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-mono text-sm text-zinc-400 tracking-wider flex items-center gap-2">
                <Terminal className="h-4 w-4" />
                {"// "}LIVE_LOGS
              </CardTitle>
              <span className="h-2 w-2 rounded-full bg-green-500 blink" />
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px] terminal-bg p-3 terminal-scroll">
              {logs.length === 0 ? (
                <p className="text-zinc-600 font-mono text-sm">No logs yet...</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, index) => (
                    <div key={log.id || index} className="flex items-start gap-2 text-sm slide-in" style={{ animationDelay: `${index * 50}ms` }}>
                      {getLogIcon(log.level)}
                      <span className="text-zinc-500 font-mono text-xs">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                      <span className={`font-mono text-xs ${
                        log.level === "SUCCESS" ? "text-green-400" :
                        log.level === "ERROR" ? "text-red-400" :
                        log.level === "WARNING" ? "text-yellow-400" :
                        "text-zinc-300"
                      }`}>
                        {log.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Bot Control Page
const BotControl = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bot/status`);
      setStatus(res.data);
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleControl = async (action) => {
    setLoading(true);
    try {
      await axios.post(`${API}/bot/${action}`);
      toast.success(`Bot ${action === "start" ? "started" : action === "stop" ? "stopped" : action === "pause" ? "paused" : "resumed"}`);
      fetchStatus();
    } catch (error) {
      toast.error(`Failed to ${action} bot`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="bot-control">
      <h2 className="font-mono text-2xl font-bold tracking-tight text-white">
        {"> "}BOT_CONTROL
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Power Control */}
        <Card className="card-dark" data-testid="power-control">
          <CardHeader>
            <CardTitle className="font-mono text-sm text-zinc-400 tracking-wider">
              {"// "}POWER_CONTROL
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center py-8">
            <button
              onClick={() => handleControl(status?.is_running ? "stop" : "start")}
              disabled={loading}
              data-testid="power-button"
              className={`h-32 w-32 rounded-full border-4 flex items-center justify-center transition-all duration-300 ${
                status?.is_running 
                  ? "border-green-500 bg-green-500/10 hover:bg-green-500/20 glow-green" 
                  : "border-zinc-700 bg-zinc-900 hover:border-zinc-600"
              }`}
            >
              <Power className={`h-16 w-16 ${status?.is_running ? "text-green-500" : "text-zinc-500"}`} />
            </button>
            <p className="mt-6 font-mono text-lg text-white">
              {status?.is_running ? (status?.is_paused ? "PAUSED" : "RUNNING") : "STOPPED"}
            </p>
            
            {status?.is_running && (
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => handleControl(status?.is_paused ? "resume" : "pause")}
                  disabled={loading}
                  data-testid="pause-resume-btn"
                  className="border-zinc-700 hover:border-zinc-600"
                >
                  {status?.is_paused ? (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Resume
                    </>
                  ) : (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      Pause
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleControl("stop")}
                  disabled={loading}
                  data-testid="stop-btn"
                  className="bg-red-900/30 border border-red-500/50 hover:bg-red-900/50"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Stop
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Session Stats */}
        <Card className="card-dark" data-testid="session-stats">
          <CardHeader>
            <CardTitle className="font-mono text-sm text-zinc-400 tracking-wider">
              {"// "}SESSION_STATS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 font-mono mb-2">Total Tasks</p>
                <p className="text-4xl font-mono font-bold text-white">{status?.total_tasks || 0}</p>
              </div>
              <div className="p-6 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <p className="text-xs text-zinc-500 font-mono mb-2">Pending</p>
                <p className="text-4xl font-mono font-bold text-yellow-500">{status?.pending_tasks || 0}</p>
              </div>
              <div className="p-6 bg-green-900/20 rounded-lg border border-green-500/30">
                <p className="text-xs text-green-400 font-mono mb-2">Success</p>
                <p className="text-4xl font-mono font-bold text-green-500">{status?.success_count || 0}</p>
              </div>
              <div className="p-6 bg-red-900/20 rounded-lg border border-red-500/30">
                <p className="text-xs text-red-400 font-mono mb-2">Failed</p>
                <p className="text-4xl font-mono font-bold text-red-500">{status?.fail_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Target Links Page
const TargetLinks = () => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTarget, setNewTarget] = useState({ name: "", url: "" });
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchTargets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/targets`);
      setTargets(res.data);
    } catch (error) {
      console.error("Error fetching targets:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const handleAddTarget = async () => {
    if (!newTarget.name || !newTarget.url) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      await axios.post(`${API}/targets`, newTarget);
      toast.success("Target added successfully");
      setNewTarget({ name: "", url: "" });
      setDialogOpen(false);
      fetchTargets();
    } catch (error) {
      toast.error("Failed to add target");
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.patch(`${API}/targets/${id}/toggle`);
      fetchTargets();
    } catch (error) {
      toast.error("Failed to toggle target");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/targets/${id}`);
      toast.success("Target deleted");
      fetchTargets();
    } catch (error) {
      toast.error("Failed to delete target");
    }
  };

  const handleCreateTask = async (targetId, count = 1) => {
    try {
      if (count === 1) {
        await axios.post(`${API}/tasks/create?target_id=${targetId}`);
        toast.success("Task created");
      } else {
        await axios.post(`${API}/tasks/bulk-create?target_id=${targetId}&count=${count}`);
        toast.success(`${count} tasks created`);
      }
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="target-links">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-2xl font-bold tracking-tight text-white">
          {"> "}TARGET_LINKS
        </h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-target-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Target
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="add-target-dialog">
            <DialogHeader>
              <DialogTitle className="font-mono text-white">Add New Target</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs text-zinc-500 font-mono mb-2 block">NAME</label>
                <Input
                  value={newTarget.name}
                  onChange={(e) => setNewTarget({ ...newTarget, name: e.target.value })}
                  placeholder="e.g. ASUS Partner"
                  className="input-terminal"
                  data-testid="target-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 font-mono mb-2 block">URL</label>
                <Input
                  value={newTarget.url}
                  onChange={(e) => setNewTarget({ ...newTarget, url: e.target.value })}
                  placeholder="https://example.com/register"
                  className="input-terminal"
                  data-testid="target-url-input"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="ghost" className="text-zinc-400">Cancel</Button>
              </DialogClose>
              <Button onClick={handleAddTarget} className="btn-primary" data-testid="confirm-add-target">
                Add Target
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-green-500 animate-spin" />
        </div>
      ) : targets.length === 0 ? (
        <Card className="card-dark">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target className="h-12 w-12 text-zinc-600 mb-4" />
            <p className="text-zinc-500 font-mono">No targets added yet</p>
            <p className="text-zinc-600 text-sm mt-1">Click "Add Target" to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {targets.map((target, index) => (
            <Card 
              key={target.id} 
              className={`card-dark slide-in ${!target.is_active ? "opacity-50" : ""}`}
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`target-card-${target.id}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={target.is_active}
                      onCheckedChange={() => handleToggle(target.id)}
                      data-testid={`target-toggle-${target.id}`}
                    />
                    <div>
                      <h3 className="font-mono font-bold text-white">{target.name}</h3>
                      <p className="text-xs text-zinc-500 font-mono truncate max-w-md">{target.url}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-zinc-500 font-mono">REGISTRATIONS</p>
                      <p className="text-lg font-mono font-bold text-green-500">{target.registration_count || 0}</p>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          className="btn-secondary" 
                          disabled={!target.is_active}
                          data-testid={`create-task-dropdown-${target.id}`}
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Create Tasks
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                        <DropdownMenuItem onClick={() => handleCreateTask(target.id, 1)} className="font-mono text-sm cursor-pointer">
                          1 Task
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCreateTask(target.id, 5)} className="font-mono text-sm cursor-pointer">
                          5 Tasks
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCreateTask(target.id, 10)} className="font-mono text-sm cursor-pointer">
                          10 Tasks
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCreateTask(target.id, 20)} className="font-mono text-sm cursor-pointer">
                          20 Tasks
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => window.open(target.url, "_blank")}
                      className="text-zinc-500 hover:text-white"
                      data-testid={`open-url-${target.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(target.id)}
                      className="text-zinc-500 hover:text-red-500"
                      data-testid={`delete-target-${target.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Data Generator Page
const DataGenerator = () => {
  const [data, setData] = useState(null);
  const [batchData, setBatchData] = useState([]);
  const [loading, setLoading] = useState(false);

  const generatePreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/generator/preview`);
      setData(res.data);
    } catch (error) {
      toast.error("Failed to generate data");
    } finally {
      setLoading(false);
    }
  };

  const generateBatch = async (count) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/generator/batch?count=${count}`);
      setBatchData(res.data.data);
      toast.success(`Generated ${count} user profiles`);
    } catch (error) {
      toast.error("Failed to generate batch data");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  useEffect(() => {
    generatePreview();
  }, []);

  return (
    <div className="space-y-6 fade-in" data-testid="data-generator">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-2xl font-bold tracking-tight text-white">
          {"> "}DATA_GENERATOR
        </h2>
        <div className="flex gap-2">
          <Button onClick={generatePreview} disabled={loading} className="btn-secondary" data-testid="generate-single">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Regenerate
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="btn-primary" data-testid="generate-batch">
                <Zap className="h-4 w-4 mr-2" />
                Generate Batch
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
              {[5, 10, 20, 50].map((count) => (
                <DropdownMenuItem 
                  key={count} 
                  onClick={() => generateBatch(count)} 
                  className="font-mono text-sm cursor-pointer"
                >
                  {count} Users
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Single Preview */}
      <Card className="card-dark" data-testid="single-preview">
        <CardHeader>
          <CardTitle className="font-mono text-sm text-zinc-400 tracking-wider">
            {"// "}PREVIEW
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(data).map(([key, value]) => (
                <div 
                  key={key} 
                  className="p-3 bg-black/50 rounded-lg border border-zinc-800 group cursor-pointer hover:border-green-500/50 transition-colors"
                  onClick={() => copyToClipboard(value)}
                >
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mb-1">{key.replace("_", " ")}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-mono text-white truncate">{value}</p>
                    <Copy className="h-3 w-3 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 text-green-500 animate-spin" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch Results */}
      {batchData.length > 0 && (
        <Card className="card-dark" data-testid="batch-results">
          <CardHeader>
            <CardTitle className="font-mono text-sm text-zinc-400 tracking-wider">
              {"// "}BATCH_RESULTS ({batchData.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {batchData.map((user, index) => (
                  <div 
                    key={index} 
                    className="p-3 bg-black/50 rounded-lg border border-zinc-800 flex items-center justify-between slide-in"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-zinc-500 font-mono w-8">#{index + 1}</span>
                      <div>
                        <p className="font-mono text-white text-sm">{user.first_name} {user.last_name}</p>
                        <p className="text-xs text-zinc-500 font-mono">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs border-zinc-700">{user.country}</Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(JSON.stringify(user, null, 2))}
                        className="text-zinc-500 hover:text-white h-8 w-8"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Tasks Page
const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks?status=pending&limit=50`);
      setTasks(res.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, 3000);
    return () => clearInterval(interval);
  }, [fetchTasks]);

  const handleExecute = async (taskId) => {
    setExecuting(taskId);
    try {
      await axios.post(`${API}/tasks/${taskId}/execute`);
      toast.success("Task executed");
      fetchTasks();
    } catch (error) {
      toast.error("Task execution failed");
    } finally {
      setExecuting(null);
    }
  };

  const handleCancel = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success("Task cancelled");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to cancel task");
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-2xl font-bold tracking-tight text-white">
          {"> "}PENDING_TASKS
        </h2>
        <Button onClick={fetchTasks} className="btn-secondary" data-testid="refresh-tasks">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-green-500 animate-spin" />
        </div>
      ) : tasks.length === 0 ? (
        <Card className="card-dark">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Clock className="h-12 w-12 text-zinc-600 mb-4" />
            <p className="text-zinc-500 font-mono">No pending tasks</p>
            <p className="text-zinc-600 text-sm mt-1">Create tasks from the Targets page</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tasks.map((task, index) => (
            <Card 
              key={task.id} 
              className="card-dark slide-in"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`task-card-${task.id}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-mono font-bold text-white">{task.target_name}</h3>
                      <p className="text-xs text-zinc-500 font-mono">{task.generated_data?.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs border-yellow-500/50 text-yellow-500">
                      PENDING
                    </Badge>
                    <Button
                      onClick={() => handleExecute(task.id)}
                      disabled={executing === task.id}
                      className="btn-primary h-9"
                      data-testid={`execute-task-${task.id}`}
                    >
                      {executing === task.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Execute
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCancel(task.id)}
                      className="text-zinc-500 hover:text-red-500"
                      data-testid={`cancel-task-${task.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// History Page
const HistoryPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchHistory = useCallback(async () => {
    try {
      let url = `${API}/tasks?limit=100`;
      if (filter === "success") url += "&status=success";
      else if (filter === "failed") url += "&status=failed";
      
      const res = await axios.get(url);
      const filtered = filter === "all" 
        ? res.data.filter(t => t.status !== "pending")
        : res.data;
      setTasks(filtered);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleClearHistory = async () => {
    try {
      await axios.delete(`${API}/tasks/clear/completed`);
      toast.success("History cleared");
      fetchHistory();
    } catch (error) {
      toast.error("Failed to clear history");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/50 font-mono">SUCCESS</Badge>;
      case "failed":
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/50 font-mono">FAILED</Badge>;
      case "cancelled":
        return <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/50 font-mono">CANCELLED</Badge>;
      default:
        return <Badge variant="outline" className="font-mono">{status.toUpperCase()}</Badge>;
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="history-page">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-2xl font-bold tracking-tight text-white">
          {"> "}TASK_HISTORY
        </h2>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="btn-secondary" data-testid="filter-dropdown">
                <Settings className="h-4 w-4 mr-2" />
                Filter: {filter.toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
              <DropdownMenuItem onClick={() => setFilter("all")} className="font-mono text-sm cursor-pointer">
                All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("success")} className="font-mono text-sm cursor-pointer">
                Success Only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("failed")} className="font-mono text-sm cursor-pointer">
                Failed Only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={handleClearHistory} className="btn-destructive" data-testid="clear-history">
            <Trash2 className="h-4 w-4 mr-2" />
            Clear History
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-green-500 animate-spin" />
        </div>
      ) : tasks.length === 0 ? (
        <Card className="card-dark">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <History className="h-12 w-12 text-zinc-600 mb-4" />
            <p className="text-zinc-500 font-mono">No history yet</p>
            <p className="text-zinc-600 text-sm mt-1">Executed tasks will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tasks.map((task, index) => (
            <Card 
              key={task.id} 
              className="card-dark slide-in"
              style={{ animationDelay: `${index * 30}ms` }}
              data-testid={`history-card-${task.id}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                      task.status === "success" ? "bg-green-500/10" :
                      task.status === "failed" ? "bg-red-500/10" :
                      "bg-zinc-500/10"
                    }`}>
                      {task.status === "success" ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : task.status === "failed" ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-zinc-500" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-mono font-bold text-white">{task.target_name}</h3>
                      <p className="text-xs text-zinc-500 font-mono">{task.generated_data?.email}</p>
                      {task.result_message && (
                        <p className={`text-xs font-mono mt-1 ${
                          task.status === "success" ? "text-green-400" : "text-red-400"
                        }`}>
                          {task.result_message}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-zinc-600 font-mono">
                        {task.completed_at ? new Date(task.completed_at).toLocaleString() : "-"}
                      </p>
                    </div>
                    {getStatusBadge(task.status)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Main App
function App() {
  return (
    <div className="min-h-screen bg-[#09090b]">
      <BrowserRouter>
        <Sidebar />
        <main className="ml-64 p-6 md:p-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/control" element={<BotControl />} />
            <Route path="/targets" element={<TargetLinks />} />
            <Route path="/generator" element={<DataGenerator />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/history" element={<HistoryPage />} />
          </Routes>
        </main>
      </BrowserRouter>
      <Toaster 
        position="bottom-right" 
        theme="dark"
        toastOptions={{
          style: {
            background: '#18181b',
            border: '1px solid #27272a',
            color: '#fafafa',
            fontFamily: 'JetBrains Mono, monospace',
          },
        }}
      />
    </div>
  );
}

export default App;
